import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";

const ThemeNotificationSetting = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [sendToAll, setSendToAll] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState(1); // 1 = normal, 2 = offer
  const [code, setCode] = useState("");
  const [off, setOff] = useState("");
  const [loading, setLoading] = useState(false);

  const notificationSuggestions = [
    {
      title: "✨ New Gold Collection Launched!",
      body: "Explore our stunning new gold jewellery designs — available now in store and online.",
    },
    {
      title: "💍 Exclusive Offer for You!",
      body: "Get flat 15% off on diamond rings this weekend only. Don’t miss out!",
    },
    {
      title: "🎉 Festive Sale is Live!",
      body: "Celebrate with sparkle — up to 25% off on gold and silver ornaments.",
    },
  ];

  // Fetch users with FCM
  useEffect(() => {
    axiosInstance
      .get("/all-user-fcm")
      .then((res) => {
        if (res.data?.users) setUsers(res.data.users);
        else toast.error("No users found");
      })
      .catch(() => toast.error("Failed to load users"));
  }, []);

  // Send notification
  const handleSendNotification = async () => {
    if (!title || !body) return toast.error("Please enter both title and message");
    if (!sendToAll && !selectedUser)
      return toast.error("Please select a user or choose 'Send to All'");

    if (type === 2 && (!code || !off))
      return toast.error("Please enter coupon code and offer details");

    setLoading(true);
    try {
      const payload = sendToAll
        ? { title, body, all: true, type, code, off }
        : { title, body, userId: selectedUser, type, code, off };

      await axiosInstance.post("/send-push", payload);

      toast.success(
        type === 1
          ? "✅ Normal notification sent!"
          : "🎁 Offer notification sent with coupon!"
      );

      // reset fields
      setTitle("");
      setBody("");
      setCode("");
      setOff("");
      setSelectedUser("");
      setSendToAll(false);
      setType(1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setTitle(suggestion.title);
    setBody(suggestion.body);
    toast.success("✨ Suggestion applied!");
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="container py-4">
          <div className="mb-4">
            <h3 className="fw-bold text-primary mb-1">📢 Notification Center</h3>
            <p className="text-muted mb-0">
              Send notifications (normal or offer) to users or all users.
            </p>
          </div>

          <div className="card border-0 shadow-lg rounded-4 p-4">
            <h5 className="fw-semibold text-dark mb-3">Send New Notification</h5>

            <div className="row g-4">
              {/* Notification type */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Notification Type</label>
                <select
                  className="form-select shadow-sm"
                  value={type}
                  onChange={(e) => setType(Number(e.target.value))}
                >
                  <option value={1}>📰 Normal Notification</option>
                  <option value={2}>🎁 Offer Notification (with coupon)</option>
                </select>
              </div>

              {/* Title */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Title</label>
                <input
                  type="text"
                  className="form-control shadow-sm"
                  placeholder="Enter title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Message */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Message</label>
                <input
                  type="text"
                  className="form-control shadow-sm"
                  placeholder="Enter message"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>

              {/* Offer Fields (only visible for type 2) */}
              {type === 2 && (
                <>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Coupon Code</label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      placeholder="e.g. FESTIVE20"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Offer % or Text</label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      placeholder="e.g. 20% Off"
                      value={off}
                      onChange={(e) => setOff(e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* Suggestions */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Quick Suggestions</label>
                <select
                  className="form-select shadow-sm"
                  onChange={(e) => {
                    const selected = notificationSuggestions.find(
                      (s) => s.title === e.target.value
                    );
                    if (selected) handleSuggestionSelect(selected);
                  }}
                  defaultValue=""
                >
                  <option value="">Select a ready-made message</option>
                  {notificationSuggestions.map((s, idx) => (
                    <option key={idx} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* User select */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Select User</label>
                <select
                  disabled={sendToAll}
                  className="form-select shadow-sm"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  <option value="">Choose a user</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Send to all */}
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sendToAll"
                    checked={sendToAll}
                    onChange={() => {
                      setSendToAll(!sendToAll);
                      if (!sendToAll) setSelectedUser("");
                    }}
                  />
                  <label
                    className="form-check-label fw-semibold"
                    htmlFor="sendToAll"
                  >
                    Send to All Users
                  </label>
                </div>
              </div>
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="alert alert-info mt-4">
                <strong>Preview:</strong>
                <div className="mt-2">
                  <span className="fw-semibold text-dark">
                    {title || "No title"}
                  </span>
                  <br />
                  <small>{body || "No message"}</small>
                  {type === 2 && (
                    <div className="mt-2 text-success">
                      <small>
                        Coupon: <b>{code || "N/A"}</b> | Offer: <b>{off || "N/A"}</b>
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Button */}
            <div className="text-end mt-4">
              <button
                className="btn btn-primary px-4 py-2 shadow-sm"
                onClick={handleSendNotification}
                disabled={
                  loading || !title || !body || (!sendToAll && !selectedUser)
                }
              >
                {loading
                  ? "Sending..."
                  : type === 1
                    ? "🚀 Send Normal Notification"
                    : "🎁 Send Offer Notification"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ThemeNotificationSetting;
